import React, { useState, useRef, useEffect } from 'react';
import { useApi } from "../../api/useApi";
import './LegalCaseForm.css';
import { getIncidentApiToken, getAuthApiToken } from '../../api/tokenService';
import { useAuth } from '../../context/AuthContext';
// Add subscription imports
import { useSubscriptionGuard } from '../../hooks/useSubscriptionGuard';
import { trackUsage } from '../../services/usageService';
import { extractTextFromFile } from '../../utils/fileTextExtractor';
import { jurisdictionProfiles } from '../../utils/jurisdictionProfiles';

const LegalCaseForm = ({ onAnalysisComplete, onError, setLoading, apiUrl }) => {
  const { postWithIdToken } = useApi();
  const { user, updateUser } = useAuth();

  // Add subscription guard hook
  const { subscription, isValid: subscriptionValid, isLoading: subLoading, error: subError, usage, validateSubscription } = useSubscriptionGuard();

  const [formData, setFormData] = useState({
    caseId: `CASE-${Date.now().toString().slice(-6)}`,
    caseDescription: '',
    jurisdiction: 'India',
    caseType: 'Criminal',
  });

  const [config, setConfig] = useState({
    analysisDepth: 'standard',
    includePrecedents: true,
    languageSimple: true,
    currency: 'INR',
  });

  const [useSampleData, setUseSampleData] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [isLoadingCurrency, setIsLoadingCurrency] = useState(true);

  const fileInputRef = useRef(null);

  const currencyOptions = [
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  ];

  // Fetch user's currency from backend when component mounts
  useEffect(() => {
    const fetchUserCurrency = async () => {
      try {
        if (user?.currency) {
          console.log('Using currency from user context:', user.currency);
          setConfig(prev => ({ ...prev, currency: user.currency }));
          setIsLoadingCurrency(false);
          return;
        }

        const token = await getAuthApiToken();
        if (!token) {
          console.log('No token found, using default currency (INR)');
          setIsLoadingCurrency(false);
          return;
        }
        const authUrl = process.env.REACT_APP_AUTH_URL;
        const response = await fetch(`${authUrl}/api/auth/users/my-profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const userData = await response.json();
          if (userData.currency) {
            setConfig(prev => ({ ...prev, currency: userData.currency }));
            if (updateUser) {
              updateUser({ ...user, currency: userData.currency });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user currency:', error);
      } finally {
        setIsLoadingCurrency(false);
      }
    };

    fetchUserCurrency();
  }, [user, apiUrl, updateUser]);

  // Update user's currency preference in backend
  const updateUserCurrency = async (newCurrency) => {
    try {
      const token = await getAuthApiToken();
      const authUrl = process.env.REACT_APP_AUTH_URL;
      if (!token) {
        console.error('No authentication token found');
        alert('Please login again to change currency preference');
        return;
      }

      console.log('Updating currency to:', newCurrency);

      const response = await fetch(`${authUrl}/api/auth/users/update-currency`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ currency: newCurrency })
      });

      if (response.ok) {
        console.log('Currency updated in backend:', newCurrency);

        if (updateUser) {
          updateUser({ ...user, currency: newCurrency });
        }

        const storedUser = sessionStorage.getItem('user');
        if (storedUser) {
          const userObj = JSON.parse(storedUser);
          userObj.currency = newCurrency;
          sessionStorage.setItem('user', JSON.stringify(userObj));
        }

        alert(`Currency preference updated to ${newCurrency}`);

        window.dispatchEvent(new CustomEvent('currencyChanged', {
          detail: { currency: newCurrency }
        }));

      } else if (response.status === 401) {
        alert('Session expired. Please refresh the page and try again.');
      } else {
        const errorData = await response.json();
        console.error('Failed to update currency:', errorData);
        alert(errorData.message || 'Failed to update currency preference');
      }
    } catch (error) {
      console.error('Error updating user currency:', error);
      alert('Error updating currency preference. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));

    if (field === 'currency') {
      updateUserCurrency(value);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      onError('File size exceeds 10MB limit');
      return;
    }

    try {
      const text = await extractTextFromFile(file);
      setFormData(prev => ({
        ...prev,
        caseDescription: text
      }));
    } catch (err) {
      onError(err.message || 'Could not read file. Please paste the text instead.');
    }
  };

  const loadSampleData = () => {
    setFormData({
      caseId: 'CR-2024-00123',
      caseDescription: `On the night of 15th January 2024, the accused Mr. A was allegedly involved in a burglary at the complainant's residence. The complainant reported that cash and jewelry worth ₹5,00,000 were stolen. CCTV footage shows a person matching the accused's description entering the building at 11:30 PM. The accused was arrested the next day and has denied involvement.`,
      jurisdiction: 'India',
      caseType: 'Criminal',
    });
    setConfig(prev => ({
      ...prev,
      analysisDepth: 'deep',
      includePrecedents: true,
      languageSimple: true,
    }));
    setUseSampleData(true);
  };

  const clearForm = () => {
    setFormData({
      caseId: `CASE-${Date.now().toString().slice(-6)}`,
      caseDescription: '',
      jurisdiction: 'India',
      caseType: 'Criminal',
    });
    setConfig(prev => ({
      ...prev,
      analysisDepth: 'standard',
      includePrecedents: true,
      languageSimple: true,
    }));
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUseSampleData(false);
  };

  const getCurrencySymbol = (currencyCode) => {
    const currency = currencyOptions.find(c => c.code === currencyCode);
    return currency ? currency.symbol : '₹';
  };

  // Add validation function before analysis
  const validateBeforeAnalysis = async () => {
    if (!subscriptionValid) {
      if (subError?.type === 'EXPIRED') {
        onError(`⚠️ ${subError.message}`);
        window.dispatchEvent(new CustomEvent('openSubscription'));
      } else if (subError?.type === 'LIMIT_REACHED') {
        onError(`⚠️ ${subError.message}`);
        window.dispatchEvent(new CustomEvent('openSubscription'));
      }
      return false;
    }
    return true;
  };

  // Add expiry warning banner
  const showExpiryWarning = () => {
    if (subscription?.endDate && subscription.plan?.name !== 'free') {
      const daysUntilExpiry = Math.ceil((new Date(subscription.endDate) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry <= 7 && daysUntilExpiry > 0) {
        return (
          <div className="expiry-warning">
            ⚠️ Your subscription expires in {daysUntilExpiry} days.
            <button onClick={() => window.dispatchEvent(new CustomEvent('openSubscription'))}>
              Renew Now
            </button>
          </div>
        );
      }
    }
    return null;
  };

  // Add usage progress bar for free tier
  const showUsageProgress = () => {
    if ((!subscription || subscription.plan?.name === 'free') && usage) {
      const percentage = (usage.currentUsage / usage.limit) * 100;
      return (
        <div className="usage-progress">
          <div className="usage-info">
            <span>Free Plan Usage: {usage.currentUsage}/{usage.limit} cases this month</span>
            {percentage >= 80 && (
              <button onClick={() => window.dispatchEvent(new CustomEvent('openSubscription'))}>
                Upgrade
              </button>
            )}
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${Math.min(percentage, 100)}%` }}></div>
          </div>
        </div>
      );
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Add validation before proceeding
    const canProceed = await validateBeforeAnalysis();
    if (!canProceed) return;

    setLoading(true);
    onError(null);

    try {
      const payload = {
        case: {
          caseId: formData.caseId,
          caseDescription: formData.caseDescription,
          jurisdiction: formData.jurisdiction,
          caseType: formData.caseType,
          config: {
            analysisDepth: config.analysisDepth,
            includePrecedents: config.includePrecedents,
            languageSimple: config.languageSimple,
            preferredCurrency: config.currency,
          }
        }
      };

      console.log('Sending to API:', `${apiUrl}/api/v1/analyze-legal`);
      console.log('With currency:', config.currency);

      const idToken = await getIncidentApiToken();
      const response = await postWithIdToken(`${apiUrl}/api/v1/analyze-legal`, payload, idToken);

      // Track usage after successful analysis
      await trackUsage('incident');
      await validateSubscription();

      onAnalysisComplete(response, payload.case);
    } catch (error) {
      console.error('Legal analysis error:', error);
      onError(error.message || 'Failed to analyze case. Please check your backend connection.');
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingCurrency) {
    return (
      <div className="incident-form legal-form">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="incident-form legal-form">
      {/* Add subscription warnings at the top */}
      {showExpiryWarning()}
      {showUsageProgress()}

      <div className="form-header">
        <h2>⚖️ Initial Case Assessment</h2>
        <div className="form-actions">
          <button type="button" onClick={() => setShowConfig(!showConfig)} className="secondary-btn">
            {showConfig ? 'Hide' : 'Show'} Analysis Config
          </button>
          <button type="button" onClick={loadSampleData} className="secondary-btn">
            Load Sample Case
          </button>
          <button type="button" onClick={clearForm} className="secondary-btn">
            Clear Form
          </button>
        </div>
      </div>

      {showConfig && (
        <div className="config-section">
          <h3>⚙️ Analysis Configuration</h3>
          <div className="config-grid">
            <div className="config-group">
              <label>Analysis Depth:</label>
              <select
                value={config.analysisDepth}
                onChange={(e) => handleConfigChange('analysisDepth', e.target.value)}
              >
                <option value="quick">Quick (summary only)</option>
                <option value="standard">Standard (includes laws and next steps)</option>
                <option value="deep">Deep (includes precedents and detailed explanations)</option>
              </select>
            </div>

            <div className="config-group">
              <label>Preferred Currency:</label>
              <div className="currency-selector">
                <select
                  value={config.currency}
                  onChange={(e) => handleConfigChange('currency', e.target.value)}
                  className="currency-select"
                >
                  {currencyOptions.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.symbol} {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
                <span className="currency-preview">
                  💰 Financial values will be shown in {getCurrencySymbol(config.currency)}
                  {user?.currency && <span className="currency-source"> (Saved to your profile)</span>}
                </span>
              </div>
            </div>

            <div className="config-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={config.includePrecedents}
                  onChange={(e) => handleConfigChange('includePrecedents', e.target.checked)}
                />
                Include relevant case precedents
              </label>
            </div>

            <div className="config-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={config.languageSimple}
                  onChange={(e) => handleConfigChange('languageSimple', e.target.checked)}
                />
                Use plain-language explanations
              </label>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="caseId">Case ID *</label>
          <input
            type="text"
            id="caseId"
            name="caseId"
            value={formData.caseId}
            onChange={handleChange}
            required
            placeholder="e.g., CR-2024-00123"
          />
        </div>

        <div className="form-row">
          <div className="form-group half">
            <label htmlFor="jurisdiction">Jurisdiction</label>
            <select id="jurisdiction" name="jurisdiction" value={formData.jurisdiction} onChange={handleChange}>
              {jurisdictionProfiles.map((j) => (
                <option key={j.code} value={j.name}>{j.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group half">
            <label htmlFor="caseType">Case Type</label>
            <select id="caseType" name="caseType" value={formData.caseType} onChange={handleChange}>
              <option value="Criminal">Criminal</option>
              <option value="Civil">Civil</option>
              <option value="Corporate">Corporate</option>
              <option value="Family">Family</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <div className="field-header">
            <label htmlFor="caseDescription">Case Description *</label>
            <input
              type="file"
              ref={fileInputRef}
              accept=".txt,.doc,.docx,.pdf"
              onChange={handleFileUpload}
              className="file-upload"
            />
          </div>
          <textarea
            id="caseDescription"
            name="caseDescription"
            value={formData.caseDescription}
            onChange={handleChange}
            rows="10"
            required
            placeholder="Paste the full case description, FIR, or complaint here. You can also upload a text file."
          />
          <div className="hint">Upload .txt, .doc, .docx, or .pdf (max 10MB). For PDFs, text extraction may be limited.</div>
        </div>

        {useSampleData && (
          <div className="sample-data-notice">
            ⚠️ Using sample case data. Modify fields as needed.
          </div>
        )}

        <button type="submit" className="submit-btn">
          🔍 Analyze Case
        </button>
      </form>
    </div>
  );
};

export default LegalCaseForm;